import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { ImpactBadge, RelevanceBar, SourceLevelTag, APPL_COLOR } from "../lib/ui";
import { Filter, ExternalLink, ChevronRight } from "lucide-react";

const IMPACTS = ["Critico", "Alto", "Medio", "Basso", "Informativo"];

export default function Radar() {
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: sp.get("q") || "", authority: "", market: "", technology: "", impact: "",
  });

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/regulatory", { params });
      setItems(data.items);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="ri-fade-in">
      <PageHeader overline="Regulatory Radar"
        title="Radar Regolatorio"
        subtitle="Aggiornamenti da fonti ufficiali con Relevance e Impact calcolati sul profilo del tuo sito." />

      {/* Filters */}
      <div className="rounded-xl bg-white border border-slate-200 p-4 mb-5 flex flex-wrap items-center gap-3">
        <Filter size={16} className="text-slate-400" />
        <input data-testid="radar-search" value={filters.q} onChange={(e) => set("q", e.target.value)}
          placeholder="Cerca titolo, authority, tema..."
          className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
        <select data-testid="radar-filter-market" value={filters.market} onChange={(e) => set("market", e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Tutti i mercati</option>
          {(meta?.markets || []).map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
        </select>
        <select data-testid="radar-filter-technology" value={filters.technology} onChange={(e) => set("technology", e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Tutte le tecnologie</option>
          {["aseptic filling", "terminal sterilisation", "lyophilisation", "isolator", "rabs", "single-use systems"].map((t) =>
            <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="radar-filter-impact" value={filters.impact} onChange={(e) => set("impact", e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Tutti gli impatti</option>
          {IMPACTS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      <div className="text-xs text-slate-500 mb-3">{items.length} aggiornamenti · ordinati per rilevanza</div>

      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400">Nessun risultato con i filtri selezionati.</div>
        ) : items.map((d) => (
          <Link key={d.id} to={`/app/radar/${d.id}`} data-testid={`radar-item-${d.id}`}
            className="flex items-center gap-4 px-5 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors group">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span className="font-mono text-[0.68rem] text-slate-600 font-medium">{d.authority}</span>
                <SourceLevelTag level={d.source_level} />
                <ImpactBadge level={d.impact_rating} small />
                <span className="text-[0.68rem] font-medium px-1.5 py-0.5 rounded"
                  style={{ color: APPL_COLOR[d.applicability] || "#64748B" }}>
                  {d.applicability}
                </span>
                {d.is_demo && <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">DEMO</span>}
              </div>
              <div className="text-sm font-medium text-slate-800 leading-snug group-hover:text-blue-700">{d.title_official}</div>
              <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{d.ai_summary}</div>
              <div className="flex items-center gap-2 mt-1.5 text-[0.68rem] text-slate-400 font-mono">
                <span>{d.doc_type}</span>·<span>{d.publication_date}</span>·<span>{d.status}</span>
              </div>
            </div>
            <div className="w-32 flex-shrink-0">
              <div className="ri-overline mb-1" style={{ fontSize: "0.6rem" }}>Rilevanza</div>
              <RelevanceBar score={d.relevance_score} />
            </div>
            <ChevronRight size={18} className="text-slate-300 group-hover:text-blue-600 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}
